import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Collapse,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PersonIcon from "@material-ui/icons/Person";
import TimerIcon from "@material-ui/icons/Timer";
import managerTestApi from "api/managerTest";
import clsx from "clsx";
import { DetailTestByUser, StudentAnswers } from "models/managerTest";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { scroller } from "react-scroll";
import { toast } from "react-toastify";
import { secondsToHms } from "utils/timeConvert";

export default function DetailTest() {
  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      paddingTop: 28,
      paddingBottom: 48,
      position: "relative",
      [theme.breakpoints?.down("sm")]: {
        paddingTop: 12,
        paddingBottom: 16,
        paddingLeft: "0px !important",
        paddingRight: "0px !important",
      },
    },
    mobileNavigationBox: {
      display: "none",
      [theme.breakpoints?.down("sm")]: {
        display: "block",
        position: "sticky",
        top: 68,
        zIndex: 990,
        backgroundColor: "#FFFFFF",
        borderRadius: 16,
        border: "1px solid #E2E8F0",
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.08)",
        padding: "12px 16px",
        marginBottom: 20,
      },
    },
    mobileNavigationHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      cursor: "pointer",
    },
    horizontalScrollGrid: {
      display: "flex",
      overflowX: "auto",
      gap: 8,
      padding: "10px 2px 4px 2px",
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    horizontalBadge: {
      minWidth: 38,
      height: 38,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: 13,
      cursor: "pointer",
      flexShrink: 0,
      transition: "all 0.2s ease",
    },
    backButton: {
      marginBottom: 20,
      color: "#475569",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 14px",
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#F1F5F9",
        color: "#0F172A",
        transform: "translateX(-2px)",
      },
    },
    headerBanner: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      borderRadius: 24,
      padding: "32px 36px",
      color: "#FFFFFF",
      marginBottom: 28,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.25)",
      [theme.breakpoints?.down("sm")]: {
        padding: "20px 18px",
        borderRadius: 16,
      },
    },
    headerGlow: {
      position: "absolute",
      top: "-40%",
      right: "-10%",
      width: 350,
      height: 350,
      background: "radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, rgba(99, 102, 241, 0) 70%)",
      pointerEvents: "none",
    },
    headerBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(8px)",
      padding: "4px 14px",
      borderRadius: 20,
      fontSize: "0.75rem",
      fontWeight: 800,
      color: "#818CF8",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: 12,
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },
    headerTitle: {
      fontSize: "1.75rem",
      fontWeight: 900,
      color: "#FFFFFF",
      marginBottom: 16,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1.25rem",
      },
    },
    metricGroup: {
      display: "flex",
      alignItems: "center",
      gap: 16,
      flexWrap: "wrap",
    },
    metricPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderRadius: 12,
      padding: "8px 16px",
      fontSize: "0.875rem",
      fontWeight: 800,
      color: "#FFFFFF",
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },
    desktopSidebarGrid: {
      [theme.breakpoints?.down("sm")]: {
        display: "none",
      },
    },
    sidebarCard: {
      position: "sticky",
      top: 100,
      padding: 24,
      borderRadius: 20,
      border: "1px solid #E2E8F0",
      backgroundColor: "#FFFFFF",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
    },
    sidebarTitle: {
      fontSize: "1.05rem",
      fontWeight: 800,
      color: "#0F172A",
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 20,
    },
    progressBox: {
      marginBottom: 24,
    },
    progressStats: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "0.85rem",
      fontWeight: 700,
      color: "#64748B",
      marginBottom: 8,
    },
    questionGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)",
      gap: 10,
      marginBottom: 24,
    },
    questionBadge: {
      aspectRatio: "1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 800,
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
      },
    },
    questionBadgeCorrect: {
      backgroundColor: "#10B981",
      color: "#FFFFFF",
      boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
    },
    questionBadgeIncorrect: {
      backgroundColor: "#EF4444",
      color: "#FFFFFF",
      boxShadow: "0 2px 8px rgba(239, 68, 68, 0.25)",
    },
    legendBox: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      borderTop: "1px solid #E2E8F0",
      paddingTop: 16,
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: "0.8rem",
      fontWeight: 700,
      color: "#64748B",
    },
    legendDot: {
      width: 12,
      height: 12,
      borderRadius: 4,
    },
    questionCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: 28,
      border: "1px solid #E2E8F0",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
      marginBottom: 20,
      transition: "all 0.2s ease",
      [theme.breakpoints?.down("sm")]: {
        padding: 18,
        borderRadius: 16,
      },
    },
    questionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 14,
      gap: 12,
      flexWrap: "wrap",
    },
    questionHeaderLeft: {
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    questionBadgeText: {
      backgroundColor: "#EEF2FF",
      color: "#4F46E5",
      fontWeight: 900,
      fontSize: "0.825rem",
      padding: "4px 12px",
      borderRadius: 10,
      letterSpacing: "0.04em",
    },
    questionText: {
      fontSize: "1.1rem",
      fontWeight: 800,
      color: "#0F172A",
      lineHeight: 1.5,
      marginBottom: 18,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "0.95rem",
      },
    },
    formAnswers: {
      marginTop: 12,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    answerOptionBox: {
      display: "flex",
      alignItems: "center",
      padding: "10px 16px",
      borderRadius: 12,
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      backgroundColor: "#FFFFFF",
    },
    answerOptionBoxCorrect: {
      backgroundColor: "#ECFDF5",
      borderColor: "#A7F3D0",
      boxShadow: "0 2px 8px rgba(16, 185, 129, 0.08)",
    },
    answerOptionBoxWrong: {
      backgroundColor: "#FEF2F2",
      borderColor: "#FCA5A5",
      boxShadow: "0 2px 8px rgba(239, 68, 68, 0.08)",
    },
    answerContent: {
      fontSize: 14,
      color: "#334155",
      fontWeight: 500,
      marginLeft: 8,
    },
    scrollControlBox: {
      position: "fixed",
      right: 28,
      bottom: 32,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      zIndex: 100,
      [theme.breakpoints?.down("sm")]: {
        right: 16,
        bottom: 20,
      },
    },
    scrollBtn: {
      width: 44,
      height: 44,
      borderRadius: "50%",
      backgroundColor: "#FFFFFF",
      color: "#4F46E5",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
      border: "1px solid #E2E8F0",
      minWidth: 0,
      padding: 0,
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#4F46E5",
        color: "#FFFFFF",
      },
    },
    btn_submit: {
      background: "linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)",
      borderRadius: 12,
      padding: "12px 32px",
      color: "#FFFFFF",
      fontWeight: 800,
      textTransform: "none",
      border: "none",
      cursor: "pointer",
      boxShadow: "0 4px 14px rgba(79, 70, 229, 0.3)",
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 6px 20px rgba(79, 70, 229, 0.4)",
      },
    },
    typingText: {
      fontSize: "1rem",
      fontWeight: 700,
      color: "#1E293B",
      backgroundColor: "#F1F5F9",
      padding: "12px 16px",
      borderRadius: 12,
      border: "1px solid #E2E8F0",
      marginBottom: 10,
    },
    typingCorrectAnswer: {
      fontSize: "0.9rem",
      fontWeight: 700,
      color: "#16A34A",
      marginTop: 6,
    },
  }));

  const { t } = useTranslation();
  const classes = useStyles();
  const params = useParams();
  const location: any = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down("sm"));
  const [detailTestByUser, setDetailTestByUser] = useState<DetailTestByUser>();
  const [scoreList, setScoreList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent.tagName !== "MAIN") {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, [loading]);

  const handleFetchDetailTestByUser = async () => {
    try {
      setLoading(true);
      const res: DetailTestByUser = await managerTestApi.getDetailTestByUser(params.id);
      setDetailTestByUser(res);
      const scores = res?.submissionDetails?.studentAnswers?.map((x) => ({
        question_id: x.questionId,
        score: x.typingPoint,
      })) || [];
      setScoreList(scores);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchDetailTestByUser();
  }, [params]);

  const handleScrollToTop = () => {
    const element = document.getElementById("header-banner");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleScrollToBottom = () => {
    const element = document.getElementById("footer-scroll-marker");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  const handleSelectQuestion = (index: number) => {
    const element = document.getElementById(`question-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleChangeScore = (event: ChangeEvent<HTMLInputElement>, question: StudentAnswers) => {
    const foundItem = scoreList.findIndex((n) => n.question_id === question.questionId);
    if (foundItem !== -1) {
      const newItem = [...scoreList];
      newItem[foundItem].score = Number(event.target.value);
      setScoreList(newItem);
    }
  };

  const handleEditTest = async () => {
    const maxPoint = detailTestByUser?.submissionDetails?.maxPoint;
    const totalPointChange = scoreList.reduce((x, y) => x + Number(y.score), 0);
    if (maxPoint && totalPointChange > maxPoint) {
      toast.error(`${t("managerTest.vuilongnhaptongsodiemnhohonhoacbangtongdiembaithi")}`);
      return;
    }
    try {
      const res = await managerTestApi.updateExamScore({
        submission_details_id: params.id,
        score_list: scoreList,
      });
      if (res.responseCode === "00") {
        toast.success(`${t("managerTest.capnhatdiemthanhcong")}`);
        handleFetchDetailTestByUser();
      } else if (res.responseDescription === "MISSING_MANDATORY_VALUE") {
        toast.error(`${t("managerTest.vuilongnhapduthongtin")}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress style={{ color: "#4F46E5" }} size={50} />
      </Box>
    );
  }

  const totalPoint = detailTestByUser?.submissionDetails?.point ?? 0;
  const maxPoint = detailTestByUser?.submissionDetails?.maxPoint ?? 0;
  const examTitle = detailTestByUser?.submissionDetails?.exam?.examTitle || "Chi tiết kết quả bài làm";
  const durationSec = detailTestByUser?.submissionDetails?.duration || 0;
  const studentName = detailTestByUser?.submissionDetails?.student?.name || "Học viên";
  const answersList = detailTestByUser?.submissionDetails?.studentAnswers || [];

  const correctAnswersCount = answersList.filter((a) => a.isCorrect).length;
  const progressPercent = answersList.length > 0 ? (correctAnswersCount / answersList.length) * 100 : 0;

  return (
    <Container ref={containerRef} maxWidth="lg" className={classes.container}>
      <Button
        startIcon={<ArrowBackIcon />}
        className={classes.backButton}
        onClick={() => navigate(-1)}
      >
        Quay lại
      </Button>

      <Box className={classes.headerBanner} id="header-banner">
        <Box className={classes.headerGlow} />
        <Box className={classes.headerBadge}>
          <HelpOutlineIcon style={{ fontSize: 16 }} />
          BẢNG ĐÁP ÁN & CHI TIẾT BÀI LÀM (GIÁO VIÊN)
        </Box>

        <Typography className={classes.headerTitle}>{examTitle}</Typography>

        <Box className={classes.metricGroup}>
          <Box className={classes.metricPill} style={{ backgroundColor: "rgba(16, 185, 129, 0.2)", borderColor: "rgba(16, 185, 129, 0.4)" }}>
            <EmojiEventsIcon style={{ color: "#34D399", fontSize: 18 }} />
            Điểm số: <span style={{ color: "#6EE7B7", fontSize: "1.05rem" }}>{totalPoint} / {maxPoint} điểm</span>
          </Box>

          <Box className={classes.metricPill}>
            <TimerIcon style={{ color: "#818CF8", fontSize: 18 }} />
            Thời gian: {secondsToHms(durationSec)}
          </Box>

          <Box className={classes.metricPill}>
            <PersonIcon style={{ color: "#93C5FD", fontSize: 18 }} />
            Thí sinh: {studentName}
          </Box>
        </Box>
      </Box>

      {/* Mobile Floating Sticky Question Navigation Bar */}
      <Box className={classes.mobileNavigationBox}>
        <Box
          className={classes.mobileNavigationHeader}
          onClick={() => setMobileExpanded((prev) => !prev)}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AssignmentTurnedInIcon color="primary" fontSize="small" />
            <Typography style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>
              Danh sách câu hỏi
            </Typography>
            <Chip
              label={`${correctAnswersCount}/${answersList.length}`}
              size="small"
              style={{
                backgroundColor: "#10B981",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 11,
                height: 22,
              }}
            />
          </Box>
          <IconButton size="small" style={{ padding: 4 }}>
            {mobileExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Collapsed Horizontal Scroll Row (Default on mobile) */}
        {!mobileExpanded && (
          <Box className={classes.horizontalScrollGrid}>
            {answersList.map((answer: StudentAnswers, index: number) => {
              return (
                <Box
                  key={answer.id || index}
                  onClick={() => handleSelectQuestion(index)}
                  className={clsx(
                    classes.horizontalBadge,
                    answer.isCorrect ? classes.questionBadgeCorrect : classes.questionBadgeIncorrect
                  )}
                >
                  {index + 1}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Expanded Full View */}
        <Collapse in={mobileExpanded}>
          <Box style={{ paddingTop: 12 }}>
            <Box className={classes.progressBox}>
              <Box className={classes.progressStats}>
                <span>Tiến độ trả lời đúng</span>
                <span>{correctAnswersCount} / {answersList.length} câu</span>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                style={{ height: 8, borderRadius: 4, backgroundColor: "#E2E8F0" }}
              />
            </Box>

            <Box className={classes.questionGrid}>
              {answersList.map((answer: StudentAnswers, index: number) => {
                return (
                  <Box
                    key={answer.id || index}
                    onClick={() => {
                      handleSelectQuestion(index);
                      setMobileExpanded(false);
                    }}
                    className={clsx(
                      classes.questionBadge,
                      answer.isCorrect ? classes.questionBadgeCorrect : classes.questionBadgeIncorrect
                    )}
                  >
                    {index + 1}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Main Grid Layout: Left Sidebar + Right Questions */}
      <Grid container spacing={3}>
        {/* Left Column: Question Navigator */}
        <Grid item xs={12} md={4} lg={3} className={classes.desktopSidebarGrid}>
          <Paper className={classes.sidebarCard} elevation={0}>
            <Typography className={classes.sidebarTitle}>
              <AssignmentTurnedInIcon color="primary" />
              DANH SÁCH CÂU HỎI
            </Typography>

            <Box className={classes.progressBox}>
              <Box className={classes.progressStats}>
                <span>Số câu đúng</span>
                <span>{correctAnswersCount} / {answersList.length} câu</span>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                style={{ height: 8, borderRadius: 4, backgroundColor: "#E2E8F0" }}
              />
            </Box>

            <Box className={classes.questionGrid}>
              {answersList.map((answer: StudentAnswers, index: number) => (
                <Box
                  key={answer.id || index}
                  onClick={() => handleSelectQuestion(index)}
                  className={clsx(
                    classes.questionBadge,
                    answer.isCorrect ? classes.questionBadgeCorrect : classes.questionBadgeIncorrect
                  )}
                >
                  {index + 1}
                </Box>
              ))}
            </Box>

            <Box className={classes.legendBox}>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: "#10B981" }} />
                <span>Trả lời đúng ({correctAnswersCount})</span>
              </Box>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: "#EF4444" }} />
                <span>Trả lời sai ({answersList.length - correctAnswersCount})</span>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Column: Question Content Detail */}
        <Grid item xs={12} md={8} lg={9}>
          {answersList.map((answer: StudentAnswers, index: number) => {
            const isUserCorrect = answer.isCorrect;

            return (
              <Paper key={answer.id || index} id={`question-${index}`} className={classes.questionCard} elevation={0}>
                <Box className={classes.questionHeader}>
                  <Box className={classes.questionHeaderLeft}>
                    <Box className={classes.questionBadgeText}>
                      {t("test.cau")} {index + 1} / {answersList.length}
                    </Box>
                    {location?.state?.editPoint ? (
                      <TextField
                        label="Nhập điểm"
                        name="score"
                        size="small"
                        variant="outlined"
                        type="number"
                        autoComplete="off"
                        style={{ width: 110 }}
                        defaultValue={
                          scoreList.find((x) => x.question_id === answer.questionId)?.score ?? 0
                        }
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          handleChangeScore(event, answer)
                        }
                      />
                    ) : (
                      <Chip
                        label={`Điểm: ${answer.typingPoint || 0}`}
                        size="small"
                        style={{ backgroundColor: "#EFF6FF", color: "#1E40AF", fontWeight: 800 }}
                      />
                    )}
                  </Box>

                  {isUserCorrect ? (
                    <Chip
                      icon={<CheckCircleIcon style={{ color: "#16A34A" }} />}
                      label="CHÍNH XÁC"
                      size="small"
                      style={{ backgroundColor: "#F0FDF4", color: "#16A34A", fontWeight: 800 }}
                    />
                  ) : (
                    <Chip
                      icon={<CancelIcon style={{ color: "#DC2626" }} />}
                      label="CHƯA CHÍNH XÁC"
                      size="small"
                      style={{ backgroundColor: "#FEF2F2", color: "#DC2626", fontWeight: 800 }}
                    />
                  )}
                </Box>

                <Typography className={classes.questionText}>
                  {answer.question.content}
                </Typography>

                {(answer?.question?.imageFileList?.length || 0) > 0 && (
                  <Box
                    display="grid"
                    gridTemplateColumns={answer?.question?.imageFileList?.length === 1 ? "1fr" : "1fr 1fr"}
                    gridGap={10}
                    mb={2}
                  >
                    {answer.question.imageFileList.map((imgItem: any) => (
                      <img
                        key={imgItem.id}
                        style={{
                          width: "100%",
                          borderRadius: 12,
                          maxHeight: "350px",
                          objectFit: "contain",
                          border: "1px solid #E2E8F0",
                        }}
                        src={imgItem.imageFile.url}
                        alt="Question visual"
                  />
                ))}
              </Box>
            )}

            <Box>
              {detailTestByUser?.submissionDetails?.exam?.type === "TYPING" ? (
                <Box>
                  <Typography className={classes.typingText}>
                    Bài làm học sinh: {answer.typing || "(Bỏ trống)"}
                  </Typography>
                  <Typography className={classes.typingCorrectAnswer}>
                    {t("managerTest.dapandung")}: {answer.question?.answers?.[0]?.content}
                  </Typography>
                </Box>
              ) : (
                <RadioGroup name={`choice-${answer.id}`}>
                  <Box className={classes.formAnswers}>
                    {answer.question.answers.map((item) => {
                      const isSelectedByStudent = answer.choice && String(answer.choice) === String(item.id);
                      const isRightChoice = Boolean(item.isRightAnswer);

                      return (
                        <Box
                          key={item.id}
                          className={clsx(
                            classes.answerOptionBox,
                            isRightChoice && classes.answerOptionBoxCorrect,
                            isSelectedByStudent && !isRightChoice && classes.answerOptionBoxWrong
                          )}
                        >
                          <Radio
                            color="primary"
                            checked={isSelectedByStudent || isRightChoice}
                            style={{
                              color: isRightChoice
                                ? "#10B981"
                                : isSelectedByStudent
                                ? "#EF4444"
                                : "rgba(0, 0, 0, 0.24)",
                            }}
                            disabled
                          />
                          <Typography
                            className={classes.answerContent}
                            style={{
                              fontWeight: isRightChoice || isSelectedByStudent ? 700 : 500,
                              color: isRightChoice
                                ? "#047857"
                                : isSelectedByStudent
                                ? "#B91C1C"
                                : "#334155",
                            }}
                          >
                            {item?.content}
                          </Typography>

                          <Box style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                            {isRightChoice && (
                              <Chip
                                label="Đáp án đúng"
                                size="small"
                                style={{ backgroundColor: "#D1FAE5", color: "#065F46", fontWeight: 800, fontSize: "0.75rem" }}
                              />
                            )}
                            {isSelectedByStudent && !isRightChoice && (
                              <Chip
                                label="Học viên chọn"
                                size="small"
                                style={{ backgroundColor: "#FEE2E2", color: "#991B1B", fontWeight: 800, fontSize: "0.75rem" }}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </RadioGroup>
              )}
            </Box>
          </Paper>
        );
      })}
        </Grid>
      </Grid>

      {location?.state?.editPoint && (
        <Box textAlign="center" mt={4} id="footer-actions">
          <Button className={classes.btn_submit} onClick={handleEditTest}>
            Cập nhật điểm số bài thi
          </Button>
        </Box>
      )}

      <Box className={classes.scrollControlBox}>
        <Tooltip title="Lên đầu trang">
          <Button className={classes.scrollBtn} onClick={handleScrollToTop}>
            <ExpandLessIcon />
          </Button>
        </Tooltip>

        <Tooltip title="Xuống cuối trang">
          <Button className={classes.scrollBtn} onClick={handleScrollToBottom}>
            <ExpandMoreIcon />
          </Button>
        </Tooltip>
      </Box>
      <div id="footer-scroll-marker" />
    </Container>
  );
}
